import {
  getClassesByLevelService,
  getOurClassesService,
} from "../api/landingClass.service";
import { matchesCatalogSlug, slugifyCatalogSegment } from "@/utils/catalogSlug";
import type { OurClassApiItem } from "../api/landingClass.types";

export interface ResolvedLevel2Catalog {
  level2Id: string;
  level1Id: string;
  levelTitle: string;
  categorySlug: string;
}

export interface ResolvedClassCatalog extends ResolvedLevel2Catalog {
  classId: string;
  courseSlug: string;
}

function toLevel2Match(
  category: OurClassApiItem,
  level: { id: string; name: string },
  categorySlug: string
): ResolvedLevel2Catalog {
  return {
    level2Id: level.id,
    level1Id: category.id,
    levelTitle: level.name,
    categorySlug: slugifyCatalogSegment(level.name) || categorySlug,
  };
}

/**
 * Resolve /classes/{slug} to level-2 + parent level-1.
 * When preferredLevel1Id is set, prefer the match under that parent so duplicate
 * level-2 names (e.g. "Beginner") do not resolve to the first catalog hit.
 */
export async function resolveCategorySlugToLevel2(
  categorySlug: string,
  preferredLevel1Id?: string
): Promise<ResolvedLevel2Catalog | null> {
  if (!categorySlug.trim()) return null;

  const preferred = preferredLevel1Id?.trim() || "";

  try {
    const response = await getOurClassesService();
    const categories = response.data ?? [];

    const preferredCategory = preferred
      ? categories.find((category) => category.id === preferred)
      : undefined;

    // Known parent → search only there. Unknown preferred id → full scan (legacy).
    const searchIn =
      preferred && preferredCategory
        ? [preferredCategory]
        : categories;

    for (const category of searchIn) {
      for (const level of category.our_levels ?? []) {
        const levelMatches =
          matchesCatalogSlug(level.id, categorySlug) ||
          matchesCatalogSlug(level.name, categorySlug);

        if (!levelMatches) continue;

        return toLevel2Match(category, level, categorySlug);
      }

      const categoryMatches =
        matchesCatalogSlug(category.id, categorySlug) ||
        matchesCatalogSlug(category.name, categorySlug);

      if (!categoryMatches) continue;

      const firstLevel = category.our_levels?.[0];
      if (!firstLevel) continue;

      return toLevel2Match(category, firstLevel, categorySlug);
    }
  } catch {
    return null;
  }

  return null;
}

export async function resolveCourseSlugsToClass(
  categorySlug: string,
  courseSlug: string,
  preferredLevel1Id?: string
): Promise<ResolvedClassCatalog | null> {
  const level = await resolveCategorySlugToLevel2(
    categorySlug,
    preferredLevel1Id
  );
  if (!level || !courseSlug.trim()) return null;

  try {
    const listing = await getClassesByLevelService(level.level2Id, {
      level1Id: level.level1Id,
      page: 1,
      pageSize: 100,
    });

    const course = (listing.data ?? []).find(
      (item) =>
        matchesCatalogSlug(item.id, courseSlug) ||
        matchesCatalogSlug(item.name, courseSlug)
    );

    if (!course) return null;

    return {
      ...level,
      classId: course.id,
      courseSlug: slugifyCatalogSegment(course.name) || courseSlug,
    };
  } catch {
    return null;
  }
}
